import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  IconButton,
  Chip,
  Tooltip,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import axios from '../../api/client';
import { toast } from 'react-toastify';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';

const RepairCategoryManagement = () => {
  const [categories, setCategories] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, reset,
    rows: visibleCategories, resultCount, totalCount
  } = useTableSearch(categories, {
    searchFields: ['name', 'description'],
    filters: {}
  });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/repair-categories');
      setCategories(res.data || []);
    } catch (error) {
      toast.error('Error fetching repair categories');
    }
  };

  const handleOpen = (category = null) => {
    if (category) {
      setEditing(category._id);
      setFormData({ name: category.name, description: category.description || '' });
    } else {
      setEditing(null);
      setFormData({ name: '', description: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditing(null);
    setFormData({ name: '', description: '' });
  };

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast.error('Category name is required');
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        await axios.put(`/repair-categories/${editing}`, formData);
        toast.success('Repair category updated');
      } else {
        await axios.post('/repair-categories', formData);
        toast.success('Repair category created');
      }
      fetchCategories();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error saving repair category');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`Delete the "${category.name}" category? If any repairs already use it, it will be deactivated instead so those records stay intact.`)) {
      return;
    }

    try {
      const res = await axios.delete(`/repair-categories/${category._id}`);
      // The server hard-deletes only unused categories; otherwise it deactivates and
      // explains why, so surface its message rather than assuming an outcome.
      toast.success(res.data?.message || 'Repair category deleted');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error deleting repair category');
    }
  };

  const handleReactivate = async (category) => {
    try {
      await axios.put(`/repair-categories/${category._id}`, { isActive: true });
      toast.success('Repair category reactivated');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error reactivating repair category');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          Repair Categories
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Add Category
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        These categories appear in the dropdown when staff log a repair. Only active
        categories are offered for new repairs; deactivating one keeps it on existing
        records but removes it from the list.
      </Alert>

      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Category name"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleCategories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No repair categories yet. Click "Add Category" to create one.
                </TableCell>
              </TableRow>
            ) : (
              visibleCategories.map((category) => (
                <TableRow key={category._id}>
                  <TableCell sx={{ fontWeight: 500 }}>{category.name}</TableCell>
                  <TableCell>{category.description || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={category.isActive ? 'Active' : 'Inactive'}
                      color={category.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpen(category)} color="primary">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    {category.isActive ? (
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(category)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivate">
                        <IconButton size="small" onClick={() => handleReactivate(category)} color="success">
                          <UndoIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Repair Category' : 'Add Repair Category'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Category Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="e.g. Plumbing, Electrical, Roofing"
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={2}
              helperText="Optional — helps staff pick the right category"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={loading || !formData.name.trim()}>
            {loading ? 'Saving...' : editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default RepairCategoryManagement;
